import React from 'react';
import PropTypes from 'prop-types';
import clsx from '@strava/ui/clsx';
import I18n from 'utils/I18n';
import { snakeCase } from 'lodash-es';
import { trackV2 } from 'utils/analytics';
import StravaLogo from '@strava/ui/StravaLogo';
import { color } from '@strava/ui-tokens/js';
import CPRAComplianceCTA from '@strava/cpra-compliance-cta';
import StravaPackageContextProviders from '../../../StravaPackageContextProviders';
import styles from './Footer.scss';

import facebookIcon from './images/social-facebook.svg';
import instagramIcon from './images/social-instagram.svg';
import twitterIcon from './images/social-twitter.svg';
import youtubeIcon from './images/social-youtube.svg';
import linkedinIcon from './images/social-linkedin.svg';

const I18N_PREFIX = 'components.landing_page.footer';

const MENU = Object.freeze({
  features: { url: '/features' },
  subscription: { url: '/gopremium' },
  studentDiscount: { url: '/student?origin=global_footer' },
  routes: { url: '/routes/hiking/usa' },
  paris2023Marathon: { url: '/events/paris-marathon' },
  boston2023Marathon: { url: '/events/boston-marathon' },
  london2023Marathon: { url: '/events/london-marathon' },
  about: { url: '/about' },
  careers: { url: '/careers' },
  press: { url: 'https://press.strava.com' },
  stories: { url: 'https://stories.strava.com' },
  support: { url: 'https://support.strava.com' },
  business: { url: 'https://business.strava.com?utm_source=footer&utm_medium=referral' },
  termsAndConditions: { url: '/legal/terms' },
  privacyPolicy: { url: '/legal/privacy' }
});

const FOLLOW = Object.freeze({
  facebook: { url: 'https://www.facebook.com/Strava', logo: facebookIcon },
  instagram: { url: 'https://www.instagram.com/strava', logo: instagramIcon },
  twitter: { url: 'https://www.twitter.com/strava', logo: twitterIcon },
  youtube: { url: 'https://www.youtube.com/stravainc', logo: youtubeIcon },
  linkedin: {
    url: 'https://www.linkedin.com/company/strava-inc.',
    logo: linkedinIcon
  }
});

const GET_STARTED = Object.freeze({
  signup: {
    url: '/register/free?cta=sign-up&element=global-footer&source=website_show'
  },
  login: { url: '/login' }
});

const CURRENT_YEAR = new Date().getFullYear();

const Footer = ({ cpraComplianceProps }) => {
  const t = (key) => I18n.t(`${I18N_PREFIX}.${key}`);
  const { appContext } = cpraComplianceProps;

  const track = ({ element, link }) => {
    trackV2({
      category: 'onboarding',
      page: 'home',
      action: 'click',
      element,
      properties: {
        link: snakeCase(link),
        source: 'v2'
      }
    });
  };

  return (
    <footer>
      <div className={styles.container}>
        <div className={styles.column}>
          <StravaLogo
            alt="Strava"
            width={118}
            height={24}
            fill={color.colorCoreWhite}
          />
          <small>&copy; {CURRENT_YEAR} Strava</small>
        </div>
        <div className={clsx(styles.column, styles.navGroup)}>
          <h3>{t('menu.heading')}</h3>
          <ul className={clsx(styles.listNav, styles.column2)}>
            {Object.entries(MENU).map(([key, item]) => (
              <li key={snakeCase(key)}>
                <a
                  href={item.url}
                  onClick={() => track({ element: 'menu', link: key })}
                >
                  {t(`menu.links.${snakeCase(key)}`)}
                </a>
              </li>
            ))}{' '}
            <li>
              <StravaPackageContextProviders>
                <CPRAComplianceCTA
                  {...appContext}
                  className={styles.cpraComplianceCta}
                  i18nPrefix="microfrontends.legal"
                />
              </StravaPackageContextProviders>
            </li>
          </ul>
        </div>
        <div className={clsx(styles.column, styles.navGroup)}>
          <h3>{t('follow.heading')}</h3>
          <ul className={styles.listNav}>
            {Object.entries(FOLLOW).map(([key, item]) => (
              <li key={snakeCase(key)}>
                <div className={styles.socialEntry}>
                  <div
                    className={styles.socialIcon}
                    style={{
                      backgroundImage: `url(${item.logo})`
                    }}
                  />
                  <a
                    href={item.url}
                    onClick={() => track({ element: 'follow', link: key })}
                  >
                    {t(`follow.links.${snakeCase(key)}`)}
                  </a>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className={clsx(styles.column, styles.navGroup)}>
          <h3>{t('get_started.heading')}</h3>
          <ul className={clsx(styles.listNav)}>
            {Object.entries(GET_STARTED).map(([key, item]) => (
              <li key={snakeCase(key)}>
                <a
                  href={item.url}
                  onClick={() => track({ element: 'get_started', link: key })}
                >
                  {t(`get_started.links.${snakeCase(key)}`)}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
};

Footer.propTypes = {
  cpraComplianceProps: PropTypes.shape({
    url: PropTypes.string,
    scope: PropTypes.string,
    component: PropTypes.string,
    appContext: PropTypes.shape({})
  }).isRequired
};

export default Footer;
